/**
 * Prices for cards you are looking at rather than cards you own.
 *
 * The catalogue browser and the wish list show the same headline the
 * collection grid does. Lookups go to the price cache in waves so the first
 * rows paint before the last are asked for, and a set or era carries the sum
 * of its plain printings from set_price_totals.
 */

export const WAVE_SIZE = 40;

/** Whole dollars with a separator from $1,000 up, cents below. Nothing for nothing. */
export function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n >= 1000) return `$${Math.round(n).toLocaleString('en-US')}`;
  return `$${n.toFixed(2)}`;
}

/** Split a list into runs of `size`, in order. */
export function chunk(list, size = WAVE_SIZE) {
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}

/**
 * Ask for prices one wave at a time. `read` takes a wave of lookups and
 * resolves to a Map of id -> result; `apply` receives each wave's answers as
 * they land. Stops early when `isCancelled` says so, and never applies a
 * wave that came back after cancellation.
 */
export async function loadInWaves(lookups, { read, apply, isCancelled = () => false, size = WAVE_SIZE }) {
  let applied = 0;
  for (const wave of chunk(lookups, size)) {
    if (isCancelled()) break;
    let found;
    try { found = await read(wave); } catch { continue; }
    if (isCancelled()) break;
    apply(wave, found);
    applied += wave.length;
  }
  return applied;
}

/** The catalogue id a wish list row or a set card resolves to. */
export function tcgKey(setId, localId) {
  return `${setId}-${localId}`;
}

/** Sum totals for a list of set ids from a set_price_totals map. */
export function sumSetTotals(setIds, totals) {
  let total = 0;
  let priced = 0;
  for (const id of setIds) {
    const t = totals[id];
    if (!t) continue;
    total += Number(t.total_near_mint) || 0;
    priced += Number(t.priced_cards) || 0;
  }
  return { total, priced };
}
